/**
 * Repository seam (ARCHITECTURE §2, §3.2). All persistence lives behind this
 * interface so a hosted multi-tenant backend is a later *swap* (a new impl of
 * this interface), never a rewrite of core or UI. `state/` depends only on this
 * contract — not on IndexedDB.
 */
import type { Account, Currency, Entity, JournalEntry, TaxSettings } from "@core/index";
import type { Session, User } from "./identity";

/**
 * App-level metadata (distinct from ledger data). Persists the go-live setup
 * flag and the organization identity captured by the first-run wizard, so the
 * wizard runs exactly once and a fresh production deploy starts empty until it
 * does. See src/ui/SetupWizard.tsx.
 */
export interface AppMeta {
  /** True once the Setup Wizard has created the organization. */
  readonly setupComplete: boolean;
  /** Stable id of the organization (tenant) all ledger data is scoped to. */
  readonly orgId: string;
  /** Legal/trading name of the organization the tenant is running. */
  readonly orgName: string;
  /** Fiscal year start month, 1 (January) – 12 (December). */
  readonly fiscalYearStartMonth: number;
}

/** Default metadata for a brand-new (unseeded) store: setup not yet done. */
export const DEFAULT_META: AppMeta = {
  setupComplete: false,
  orgId: "",
  orgName: "",
  fiscalYearStartMonth: 1,
};

export interface Repository {
  /** Human-readable identifier of the backing store (e.g. "indexeddb"). */
  readonly kind: string;

  /** Ensure the store exists; seed demo data only in dev (see impl). */
  init(): Promise<void>;

  getEntities(): Promise<Entity[]>;
  saveEntity(entity: Entity): Promise<void>;
  getAccounts(): Promise<Account[]>;
  saveAccount(account: Account): Promise<void>;

  getEntries(): Promise<JournalEntry[]>;
  saveEntry(entry: JournalEntry): Promise<void>;
  deleteEntry(id: string): Promise<void>;

  getSettings(): Promise<TaxSettings>;
  saveSettings(settings: TaxSettings): Promise<void>;

  /** Read app-level metadata (setup flag + organization identity). */
  getMeta(): Promise<AppMeta>;
  saveMeta(meta: AppMeta): Promise<void>;

  /** Members of the organization (identity mandate). */
  getUsers(): Promise<User[]>;
  saveUser(user: User): Promise<void>;
  deleteUser(id: string): Promise<void>;

  /** The currently signed-in local session, or null when signed out. */
  getSession(): Promise<Session | null>;
  saveSession(session: Session | null): Promise<void>;

  /** Wipe the store; re-seed the demo dataset only in dev (see impl). */
  reset(): Promise<void>;
}

/**
 * First-run bootstrap payload for a server-backed repository. Mirrors the
 * Setup Wizard's `SetupInput` fields the server needs to create the tenant.
 */
export interface SetupRequest {
  readonly orgName: string;
  readonly baseCurrency: Currency;
  readonly fiscalYearStartMonth: number;
  readonly entityName: string;
  readonly entityKind: Entity["kind"];
  readonly ownerName: string;
  readonly ownerEmail: string;
  readonly password: string;
}

/**
 * Optional capability a repository may implement when authentication is
 * server-enforced (ARCHITECTURE §12). The local-first IndexedDB/in-memory
 * repositories do NOT implement it — the store keeps its client-side verify path
 * for those and delegates to the server here when present. This is the seam that
 * lets the Repository *data* interface stay unchanged while sign-in moves to the
 * server.
 */
export interface AuthRepository {
  /** Verify credentials on the server; on success the session cookie is set. */
  signIn(email: string, password: string): Promise<Session | null>;
  /** End the server session and clear the cookie. */
  signOut(): Promise<void>;
  /** First-run: create the organization + owner on the server and sign in. */
  completeSetup(req: SetupRequest): Promise<Session>;
}

/** Narrow a Repository to one that enforces authentication server-side. */
export function isAuthRepository(repo: Repository): repo is Repository & AuthRepository {
  const r = repo as Partial<AuthRepository>;
  return typeof r.signIn === "function" && typeof r.completeSetup === "function";
}
